import React, { useState, useEffect } from "react";
import {
  Layout, Table, Button, Tag, message, Row, Col,
  Select, Input, Dropdown, Modal, Breadcrumb
} from "antd";
import { useNavigate } from "react-router-dom";
import Headerdiv from "../../layout/header";
import Footerdiv from "../../layout/footer";
import {
  PlusOutlined, EyeOutlined, EditOutlined, ShoppingCartOutlined,
  SearchOutlined, FilterOutlined, ClearOutlined,
  DeleteOutlined, SendOutlined, MoreOutlined, PrinterOutlined,
  HomeOutlined
} from "@ant-design/icons";
import api from "../../../services/axiosInstance";
import moment from "moment";
import "../../../styles/fluidDesign.css";
import { getStatusColor } from "../../../utils/statusHelper";
import PRPreviewModal from "../../../components/common/PRPreviewModal";

const { Content } = Layout;
const { Option } = Select;

const ManagePurchaseRequests = () => {
  const navigate = useNavigate();

  const [prs, setPrs] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);

  // Print Modal State
  const [previewPrId, setPreviewPrId] = useState(null);
  const [previewVisible, setPreviewVisible] = useState(false);

  // Filter inputs (unapplied)
  const [supplierFilter, setSupplierFilter] = useState(undefined);
  const [statusFilter, setStatusFilter] = useState(undefined);
  const [searchRefFilter, setSearchRefFilter] = useState("");

  // Applied filters state
  const [appliedSupplier, setAppliedSupplier] = useState(undefined);
  const [appliedStatus, setAppliedStatus] = useState(undefined);
  const [appliedSearchRef, setAppliedSearchRef] = useState("");

  // Permission helper (matches current project pattern in navbarmenu.js)
  const isDisabled = (permName) => {
    const value = localStorage.getItem(`USERPERMISSION_${permName}`);
    if (value === null) return true;
    return value !== 'false';
  };

  const handleDeletePR = async (prId) => {
    setLoading(true);
    try {
      const res = await api.post("/inventory/pr/delete.php", { pr_id: prId });
      if (res.data && res.data.status === "success") {
        message.success(res.data.message || "Purchase Request deleted successfully.");
        loadData();
      } else {
        message.error(res.data.message || "Failed to delete Purchase Request.");
      }
    } catch (error) {
      message.error("Error deleting Purchase Request.");
    } finally {
      setLoading(false);
    }
  };

  const handleRequestPR = async (prId) => {
    setLoading(true);
    try {
      const res = await api.post("/inventory/pr/request.php", { pr_id: prId });
      if (res.data && res.data.status === "success") {
        message.success(res.data.message || "Purchase Request updated to Requested.");
        loadData();
      } else {
        message.error(res.data.message || "Failed to update Purchase Request.");
      }
    } catch (error) {
      message.error("Error updating Purchase Request.");
    } finally {
      setLoading(false);
    }
  };

  // Load Initial Data
  const loadData = async () => {
    setLoading(true);
    try {
      const resPrs = await api.get("/inventory/pr/list.php");
      if (resPrs.data && resPrs.data.status === "success") {
        setPrs(resPrs.data.dataset);
      } else {
        message.error(resPrs.data.message || "Failed to load Purchase Requests.");
      }

      const resSups = await api.get("/masterdropdown/listof_activesuppliers.php");
      if (resSups.data && resSups.data.status === "success") {
        setSuppliers(resSups.data.dataset);
      }
    } catch (error) {
      message.error("Error connecting to server.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Filter application
  const handleApplyFilters = () => {
    setAppliedSupplier(supplierFilter);
    setAppliedStatus(statusFilter);
    setAppliedSearchRef(searchRefFilter);
  };

  const handleClearFilters = () => {
    setSupplierFilter(undefined);
    setStatusFilter(undefined);
    setSearchRefFilter("");
    setAppliedSupplier(undefined);
    setAppliedStatus(undefined);
    setAppliedSearchRef("");
  };

  // Filter records locally
  const getFilteredPRs = () => {
    return prs.filter(pr => {
      const matchSupplier = !appliedSupplier || Number(pr.sup_id) === Number(appliedSupplier);
      const matchStatus = !appliedStatus ||
        (appliedStatus === "Pending"
          ? (pr.pr_status === "Pending" || pr.pr_status === "Pending Approval")
          : pr.pr_status === appliedStatus);
      const matchRef = !appliedSearchRef || (pr.pr_ref || "").toLowerCase().includes(appliedSearchRef.toLowerCase());
      return matchSupplier && matchStatus && matchRef;
    });
  };

  // Status Tag Formatter
  const renderStatusTag = (status) => {
    return <Tag color={getStatusColor(status)} style={{ fontWeight: "600" }}>{status}</Tag>;
  };

  const columns = [
    {
      title: "PR Reference",
      dataIndex: "pr_ref",
      key: "pr_ref",
      render: (text, record) => (
        <span
          style={{ fontWeight: "600", color: "var(--fluid-primary)", cursor: "pointer" }}
          onClick={() => navigate(`/inventory/pr/${record.pr_id}/view`)}
        >
          {text}
        </span>
      )
    },
    {
      title: "Supplier",
      dataIndex: "supplier_name",
      key: "supplier_name"
    },
    {
      title: "Date",
      dataIndex: "pr_date",
      key: "pr_date",
      render: (date) => date ? moment(date).format("YYYY-MM-DD") : "-"
    },
    {
      title: "Items",
      dataIndex: "total_items_count",
      key: "total_items_count",
      align: "center"
    },
    {
      title: "Grand Total",
      dataIndex: "pr_final_amount",
      key: "pr_final_amount",
      align: "right",
      render: (val) => <strong>LKR {Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
    },
    {
      title: "Status",
      dataIndex: "pr_status",
      key: "pr_status",
      render: (status) => renderStatusTag(status)
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      fixed: "right",
      width: 80,
      render: (_, record) => {
        const canConfigure = (record.pr_status === "Draft" || record.pr_status === "Rejected") && !isDisabled('pr_create');
        const canDelete = (record.pr_status === "Draft" || record.pr_status === "Pending" || record.pr_status === "Pending Approval") && !isDisabled('pr_create');
        const canRequest = record.pr_status === "Approved" && !isDisabled('pr_create');

        const items = [
          {
            key: "view",
            label: "View Details",
            icon: <EyeOutlined />,
            onClick: () => navigate(`/inventory/pr/${record.pr_id}/view`)
          },
          {
            key: "print",
            label: "Print",
            icon: <PrinterOutlined />,
            onClick: () => {
              setPreviewPrId(record.pr_id);
              setPreviewVisible(true);
            }
          }
        ];

        if (canConfigure) {
          items.push({
            key: "edit",
            label: "Edit Items",
            icon: <EditOutlined />,
            onClick: () => navigate(`/inventory/pr/${record.pr_id}/items`)
          });
        }

        if (canDelete) {
          items.push({
            key: "delete",
            label: "Delete Purchase Request",
            icon: <DeleteOutlined />,
            danger: true,
            onClick: () => {
              Modal.confirm({
                title: "Delete Purchase Request",
                content: "Are you sure you want to delete this Purchase Request?",
                okText: "Yes, Delete",
                okType: "danger",
                cancelText: "No",
                onOk: () => handleDeletePR(record.pr_id)
              });
            }
          });
        }

        if (canRequest) {
          items.push({
            key: "request",
            label: "Transition to Requested",
            icon: <SendOutlined />,
            onClick: () => {
              Modal.confirm({
                title: "Transition to Requested?",
                content: "Are you sure you want to transition this Purchase Request to Requested?",
                okText: "Yes",
                cancelText: "No",
                onOk: () => handleRequestPR(record.pr_id)
              });
            }
          });
        }

        return (
          <Dropdown menu={{ items }} trigger={["click"]} placement="bottomRight">
            <Button type="text" className="fluid-dots-btn" icon={<MoreOutlined />} />
          </Dropdown>
        );
      }
    }
  ];

  return (
    <Layout className="fluid-page">
      <Headerdiv />

      {/* Page Header with Breadcrumb - matching current project style */}
      <div className="fluid-page-header">
        <div className="floating-shape shape-1"></div>
        <div className="floating-shape shape-2"></div>
        <Row align="bottom" justify="space-between" gutter={[16, 12]}>
          <Col>
                          <Breadcrumb
                  className="fluid-breadcrumb"
                  items={[
                      { href: '/dash', title: <HomeOutlined /> },
                      { href: '/inventory-items', title: 'Inventory' },
                      { title: 'Purchase Requests' },
                  ]}
              />
            <h1 className="fluid-page-title">
              <ShoppingCartOutlined />
              Purchase Requests (PRs)
            </h1>
            <p className="fluid-page-subtitle">Create and manage purchase requests for inventory items</p>
          </Col>
          <Col xs={24} md={12} style={{ textAlign: 'right' }}>
            <div className="hero-actions" style={{ marginTop: 0, justifyContent: 'flex-end' }}>
              {!isDisabled('pr_create') && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => navigate("/inventory/pr/create")}
                  className="hero-action-btn"
                >
                  Create Purchase Request
                </Button>
              )}
            </div>
          </Col>
        </Row>
      </div>

      <Content className="fluid-content">
        {/* Table Card with filter bar on top (matches the modern list-page layout) */}
        <div className="fluid-card">
          <div className="fluid-filter-compact" style={{ marginBottom: 12 }}>
            <Row gutter={16} align="bottom" className="fluid-filter-row">
              <Col xs={24} sm={12} lg={5}>
                <div style={{ marginBottom: 6, fontWeight: 500, color: "var(--fluid-text)" }}>Supplier:</div>
                <Select
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) => {
                    const label = option?.label || option?.children;
                    if (Array.isArray(label)) {
                      return label.join('').toLowerCase().includes(input.toLowerCase());
                    }
                    return String(label || '').toLowerCase().includes(input.toLowerCase());
                  }}
                  placeholder="Choose Supplier"
                  allowClear
                  value={supplierFilter}
                  onChange={setSupplierFilter}
                  style={{ width: "100%" }}
                >
                  {suppliers.map((sup) => (
                    <Option key={sup.value} value={Number(sup.value)}>
                      {sup.label}
                    </Option>
                  ))}
                </Select>
              </Col>

              <Col xs={24} sm={12} lg={4}>
                <div style={{ marginBottom: 6, fontWeight: 500, color: "var(--fluid-text)" }}>Status:</div>
                <Select
                  placeholder="Choose Status"
                  allowClear
                  value={statusFilter}
                  onChange={setStatusFilter}
                  style={{ width: "100%" }}
                >
                  <Option value="Draft">Draft</Option>
                  <Option value="Pending">Pending</Option>
                  <Option value="Approved">Approved</Option>
                  <Option value="Requested">Requested</Option>
                  <Option value="Partially Received">Partially Received</Option>
                  <Option value="Received">Received</Option>
                  <Option value="Rejected">Rejected</Option>
                </Select>
              </Col>

              <Col xs={24} sm={12} lg={5}>
                <div style={{ marginBottom: 6, fontWeight: 500, color: "var(--fluid-text)" }}>Search PR Reference:</div>
                <Input
                  placeholder="Search PR Ref..."
                  allowClear
                  value={searchRefFilter}
                  onChange={(e) => setSearchRefFilter(e.target.value)}
                  prefix={<SearchOutlined />}
                  onPressEnter={handleApplyFilters}
                />
              </Col>

              <Col xs={24} sm={24} lg={4}>
                <div className="fluid-filter-compact-actions">
                  <Button
                    type="primary"
                    icon={<FilterOutlined />}
                    onClick={handleApplyFilters}
                    className="fluid-btn-primary"
                  >
                    Filter
                  </Button>
                  <Button
                    icon={<ClearOutlined />}
                    onClick={handleClearFilters}
                    className="fluid-btn-secondary"
                  >
                    Clear
                  </Button>
                </div>
              </Col>
            </Row>
          </div>

          <div className="fluid-table">
            <Table
              columns={columns}
              dataSource={getFilteredPRs()}
              rowKey="pr_id"
              loading={loading}
              pagination={{ pageSize: 10, showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items` }}
              bordered
              size="small"
              scroll={{ x: 'max-content' }}
            />
          </div>
        </div>
      </Content>
      <Footerdiv />

      {previewVisible && (
        <PRPreviewModal
          prId={previewPrId}
          visible={previewVisible}
          onCancel={() => setPreviewVisible(false)}
        />
      )}
    </Layout>
  );
};

export default ManagePurchaseRequests;
